// Per-route <title> and meta description content.
//
// Used by:
//  - scripts/prerender.mjs — injects these into the static HTML at build
//    time so crawlers see page-specific metadata, not a repeated homepage
//    title/description on every route.
//  - src/components/RouteMeta.jsx — updates document.title / meta
//    description on client-side navigation, so the browser tab and any
//    client-rendered fallback stay consistent with the prerendered HTML.

export const ROUTE_META = {
  '/': {
    title: 'Aircons Cape Town — Aircon Installation, Repairs & Servicing',
    description:
      'Fast, reliable aircon installation, repairs, servicing and regas for homes and businesses across Cape Town. Call for AC help today.',
  },
  '/aircon-repairs': {
    title: 'Aircon Repairs in Cape Town — Same-Day Fault Finding | Aircons Cape Town',
    description:
      'A warm room, a leak, a strange noise. We find the actual fault before we touch anything, and quote it before we fix it. Cape Town wide.',
  },
  '/aircon-installation': {
    title: 'Aircon Installation in Cape Town, Sized to the Room | Aircons Cape Town',
    description:
      'New installations and replacements for homes and businesses across greater Cape Town, from a single bedroom unit to a multi-zone office fit-out.',
  },
  '/aircon-servicing': {
    title: 'Aircon Servicing in Cape Town | Aircons Cape Town',
    description:
      'A service visit checks filters, coils, drainage and performance before small problems become expensive ones. Book aircon servicing in Cape Town.',
  },
  '/aircon-regas': {
    title: 'Aircon Regas in Cape Town — Checked Before We Charge | Aircons Cape Town',
    description:
      'Low refrigerant makes an aircon work harder and cool less. We check the system first, and regas only if it actually needs it.',
  },
  '/commercial-aircon': {
    title: 'Commercial Aircon for Cape Town Businesses | Aircons Cape Town',
    description:
      'Installation, repairs and servicing for offices, shops, restaurants and retail spaces. Tell us what your building needs and we will take it from there.',
  },
  '/emergency': {
    title: 'Aircon Down? Fast Emergency Response, Cape Town | Aircons Cape Town',
    description:
      "Server room overheating, a shop full of customers, a house that's unbearable by 2pm. Some aircon problems can't wait — we'll say how soon we can be there.",
  },
  '/about': {
    title: 'About Us — Aircon Work for Cape Town Homes & Businesses | Aircons Cape Town',
    description:
      'We install, repair, service and regas air conditioners across Cape Town. Straightforward pricing and honest advice, every time.',
  },
  '/contact': {
    title: 'Contact Us | Aircons Cape Town',
    description:
      'Give us the details and we will get back to you. Call, WhatsApp or send the form to book aircon installation, repairs, servicing or regas.',
  },
  '/aircons': {
    title: 'Aircon Units for Sale in Cape Town | Aircons Cape Town',
    description:
      'A clean list of available aircon units. Find the right fit for your room, from budget fixed-speed units to inverter and commercial systems.',
  },
  '/privacy-policy': {
    title: 'Privacy Policy | Aircons Cape Town',
    description: 'How Aircons Cape Town collects, uses and protects your personal information.',
  },
  '/terms-of-service': {
    title: 'Terms of Service | Aircons Cape Town',
    description: 'The terms and conditions that apply when you book a service with Aircons Cape Town.',
  },
  '/warranty': {
    title: 'Warranty | Aircons Cape Town',
    description: 'Warranty terms for aircon installations, repairs and units supplied by Aircons Cape Town.',
  },
  '/thank-you': {
    title: 'Thank You | Aircons Cape Town',
    description: "We've received your enquiry and will get back to you shortly.",
  },
};

// Location page content (suburb, H1 parts, unique intro paragraph per page)
// lives in src/location-pages.js — imported and re-exported here so the
// prerender/sitemap scripts and both routers keep a single import point.
import { LOCATION_PAGES, LOCATION_ROUTES } from './location-pages.js';
export { LOCATION_PAGES, LOCATION_ROUTES };

// Location routes that render the aircon-INSTALLATION template rather than
// the repairs one. Covers the /aircons-installer-*, /aircons-installation-*
// and /aircons-installations-* legacy Search Console URLs (not the main
// /aircon-installation service page, which has no trailing segment).
export const INSTALLER_ROUTE_RE = /^\/aircons-install(?:er|ations?)-/;
export function isInstallerRoute(route) {
  return INSTALLER_ROUTE_RE.test(route);
}

for (const [route, page] of Object.entries(LOCATION_PAGES)) {
  if (page.meta) {
    ROUTE_META[route] = page.meta;
  } else if (isInstallerRoute(route) && page.suburb) {
    ROUTE_META[route] = {
      title: `Aircon Installation in ${page.suburb} | Aircons Cape Town`,
      description: `New aircon installations and replacements for homes and businesses in ${page.suburb}. Sized to the room and quoted before we start.`,
    };
  } else {
    ROUTE_META[route] = page.suburb
      ? {
          title: `Aircon Repairs in ${page.suburb} — Same-Day Fault Finding | Aircons Cape Town`,
          description: `A warm room, a leak, a strange noise. We find the actual fault before we touch anything, and quote it before we fix it. Serving ${page.suburb} and surrounds.`,
        }
      : ROUTE_META['/aircon-repairs'];
  }
}

// Routes that render near-identical content to another route emit a
// canonical link pointing at the primary URL instead of themselves:
//   /aircons-installation-melkbosstrand   — duplicate slug for the
//                                           nav-linked /aircons-installer-melkbosstrand
export const CANONICAL_OVERRIDES = {
  '/aircons-installation-melkbosstrand': '/aircons-installer-melkbosstrand',
};

// Post-conversion/utility routes kept out of the sitemap and marked noindex.
export const NOINDEX_ROUTES = new Set(['/thank-you']);

// Build title/description for a product detail page (/aircons/:slug).
export function getProductMeta(product) {
  if (!product) return ROUTE_META['/aircons'];
  return {
    title: `${product.name} | Aircons Cape Town`,
    description: product.description || ROUTE_META['/aircons'].description,
  };
}

// Resolve metadata for any pathname, including dynamic /aircons/:slug routes.
// `products` is optional — pass PRODUCTS from products-data.js to resolve
// product pages; without it, falls back to the /aircons listing metadata.
export function getRouteMeta(pathname, products) {
  if (ROUTE_META[pathname]) return ROUTE_META[pathname];

  const productMatch = pathname.match(/^\/aircons\/([^/]+)\/?$/);
  if (productMatch) {
    const slug = productMatch[1];
    const product = products?.find((p) => p.slug === slug);
    return getProductMeta(product);
  }

  return ROUTE_META['/'];
}

// ---------------------------------------------------------------------------
// JSON-LD structured data (schema.org)
//
// Used by:
//  - scripts/prerender.mjs — injects a <script type="application/ld+json">
//    tag into each prerendered page's <head>.
//  - src/components/RouteMeta.jsx — keeps the same tag in sync on
//    client-side navigation.
// ---------------------------------------------------------------------------

export const SITE_URL = 'https://aircons-capetown.co.za';
export const SITE_NAME = 'Aircons Cape Town';
// Shared social image (also referenced by the LocalBusiness schema below).
export const OG_IMAGE = `${SITE_URL}/hero-big-new.webp`;

// Canonical URL form: trailing slash. Every prerendered page lives at
// <route>/index.html, so the trailing-slash URL is what the server serves
// with a 200; the no-slash form always 301-redirects (LiteSpeed ignores
// "DirectorySlash Off" in .htaccess — it only honours it in server config).
// Canonicals, schema URLs, sitemap locs and internal links all use the
// slash form so they agree with the URL that actually serves the page.

// Strip trailing slash(es) to get the canonical route key used for
// ROUTE_META / NOINDEX_ROUTES / CANONICAL_OVERRIDES lookups. The browser
// always lands on the slashed URL after the server's 301, so
// location.pathname must be normalised before any route-key lookup.
export function normalizeRoutePath(pathname) {
  const p = (pathname || '/').replace(/\/+$/, '');
  return p === '' ? '/' : p;
}

// Trailing-slash path for internal links, so crawlers following rendered
// hrefs hit the final URL directly instead of via a 301. Leaves hashes,
// the root path, file paths (/logo.svg, /sitemap.xml) and non-rooted
// hrefs untouched.
export function pageHref(path) {
  if (!path || !path.startsWith('/')) return path;
  const hashIdx = path.indexOf('#');
  const base = hashIdx === -1 ? path : path.slice(0, hashIdx);
  const hash = hashIdx === -1 ? '' : path.slice(hashIdx);
  if (base === '/' || base.endsWith('/') || /\.[a-z0-9]{1,8}$/i.test(base)) {
    return `${base}${hash}`;
  }
  return `${base}/${hash}`;
}

// Absolute canonical URL for a route — self-referencing unless the route
// renders identical content elsewhere (see CANONICAL_OVERRIDES).
export function getCanonicalUrl(pathname) {
  const key = normalizeRoutePath(pathname);
  const target = normalizeRoutePath(CANONICAL_OVERRIDES[key] ?? key);
  return target === '/' ? `${SITE_URL}/` : `${SITE_URL}${target}/`;
}


const BUSINESS_ID = `${SITE_URL}/#business`;
const WEBSITE_ID = `${SITE_URL}/#website`;

// Shared LocalBusiness entity. Other nodes reference it via { '@id': BUSINESS_ID }.
const BUSINESS_SCHEMA = {
  '@type': 'HVACBusiness',
  '@id': BUSINESS_ID,
  name: 'Aircons Cape Town',
  url: SITE_URL,
  telephone: '+27213880026',
  email: 'info@aircons-capetown.co.za',
  logo: `${SITE_URL}/aircon-cape-town-logo.svg`,
  image: `${SITE_URL}/hero-big-new.webp`,
  priceRange: 'R',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Cape Town',
    addressRegion: 'Western Cape',
    addressCountry: 'ZA',
  },
  areaServed: { '@type': 'City', name: 'Cape Town' },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    telephone: '+27213880026',
    email: 'info@aircons-capetown.co.za',
    availableLanguage: 'English',
  },
};

const SERVICE_NAMES = {
  '/aircon-repairs': 'Aircon Repairs',
  '/aircon-installation': 'Aircon Installation',
  '/aircon-servicing': 'Aircon Servicing',
  '/aircon-regas': 'Aircon Regas',
  '/commercial-aircon': 'Commercial Aircon',
  '/emergency': 'Emergency Aircon Call-Out',
};

const PAGE_TYPES = {
  '/about': 'AboutPage',
  '/contact': 'ContactPage',
};

const PAGE_NAMES = {
  '/about': 'About Us',
  '/contact': 'Contact',
  '/privacy-policy': 'Privacy Policy',
  '/terms-of-service': 'Terms of Service',
  '/warranty': 'Warranty',
  '/thank-you': 'Thank You',
};

function breadcrumb(items) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

// Returns an array of schema.org nodes for the route (combined into a single
// @graph by the caller). Empty array = no schema.
export function getRouteSchema(pathname, products) {
  const meta = getRouteMeta(pathname, products);
  const url = getCanonicalUrl(pathname);
  const crumbs = (name) =>
    breadcrumb([
      { name: 'Home', url: SITE_URL },
      { name, url },
    ]);

  if (pathname === '/') {
    return [
      BUSINESS_SCHEMA,
      {
        '@type': 'WebSite',
        '@id': WEBSITE_ID,
        url: SITE_URL,
        name: 'Aircons Cape Town',
        publisher: { '@id': BUSINESS_ID },
      },
    ];
  }

  const productMatch = pathname.match(/^\/aircons\/([^/]+)\/?$/);
  if (productMatch) {
    const product = products?.find((p) => p.slug === productMatch[1]);
    if (!product) return [];

    const graph = [
      BUSINESS_SCHEMA,
      {
        '@type': 'Product',
        name: product.name,
        url,
        image: `${SITE_URL}${encodeURI(product.image)}`,
        description: product.description,
        brand: { '@type': 'Brand', name: product.brand },
        sku: product.slug,
        category: 'Air conditioners',
        offers: {
          '@type': 'Offer',
          url,
          priceCurrency: 'ZAR',
          price: product.price,
          itemCondition: 'https://schema.org/NewCondition',
          availability: 'https://schema.org/InStock',
          seller: { '@id': BUSINESS_ID },
        },
      },
      breadcrumb([
        { name: 'Home', url: SITE_URL },
        { name: 'Aircon Units', url: `${SITE_URL}/aircons` },
        { name: product.shortName || product.name, url },
      ]),
    ];

    if (product.faqs?.length) {
      graph.push({
        '@type': 'FAQPage',
        mainEntity: product.faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      });
    }

    return graph;
  }

  if (pathname === '/aircons') {
    return [
      BUSINESS_SCHEMA,
      {
        '@type': 'CollectionPage',
        name: meta.title,
        description: meta.description,
        url,
        mainEntity: {
          '@type': 'ItemList',
          itemListElement: (products || []).map((p, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: p.name,
            url: `${SITE_URL}/aircons/${p.slug}/`,
          })),
        },
      },
      crumbs('Aircon Units'),
    ];
  }

  if (SERVICE_NAMES[pathname]) {
    return [
      BUSINESS_SCHEMA,
      {
        '@type': 'Service',
        name: SERVICE_NAMES[pathname],
        serviceType: SERVICE_NAMES[pathname],
        description: meta.description,
        url,
        provider: { '@id': BUSINESS_ID },
        areaServed: { '@type': 'City', name: 'Cape Town' },
      },
      crumbs(SERVICE_NAMES[pathname]),
    ];
  }

  return [
    BUSINESS_SCHEMA,
    {
      '@type': PAGE_TYPES[pathname] || 'WebPage',
      name: meta.title,
      description: meta.description,
      url,
      isPartOf: { '@id': WEBSITE_ID },
      about: { '@id': BUSINESS_ID },
    },
    crumbs(PAGE_NAMES[pathname] || meta.title),
  ];
}
